import type { SvgIconComponent } from '@mui/icons-material';
// Per-icon default imports (not the barrel) so the bundler only pulls the
// icons we actually curate.
import Business from '@mui/icons-material/Business';
import Domain from '@mui/icons-material/Domain';
import Apartment from '@mui/icons-material/Apartment';
import AccountBalance from '@mui/icons-material/AccountBalance';
import Storefront from '@mui/icons-material/Storefront';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import Factory from '@mui/icons-material/Factory';
import Engineering from '@mui/icons-material/Engineering';
import Cloud from '@mui/icons-material/Cloud';
import Code from '@mui/icons-material/Code';
import Memory from '@mui/icons-material/Memory';
import Hub from '@mui/icons-material/Hub';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import Bolt from '@mui/icons-material/Bolt';
import EnergySavingsLeaf from '@mui/icons-material/EnergySavingsLeaf';
import LocalHospital from '@mui/icons-material/LocalHospital';
import Science from '@mui/icons-material/Science';
import School from '@mui/icons-material/School';
import Flight from '@mui/icons-material/Flight';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Restaurant from '@mui/icons-material/Restaurant';
import Campaign from '@mui/icons-material/Campaign';
import Gavel from '@mui/icons-material/Gavel';
import Public from '@mui/icons-material/Public';
// Expansion batch (appended — never reorder/rename existing keys).
import Analytics from '@mui/icons-material/Analytics';
import DataObject from '@mui/icons-material/DataObject';
import Api from '@mui/icons-material/Api';
import Wifi from '@mui/icons-material/Wifi';
import Security from '@mui/icons-material/Security';
import HomeWork from '@mui/icons-material/HomeWork';
import RealEstateAgent from '@mui/icons-material/RealEstateAgent';
import Construction from '@mui/icons-material/Construction';
import Agriculture from '@mui/icons-material/Agriculture';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import DirectionsBoat from '@mui/icons-material/DirectionsBoat';
import Train from '@mui/icons-material/Train';
import Biotech from '@mui/icons-material/Biotech';
import Spa from '@mui/icons-material/Spa';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import LocalCafe from '@mui/icons-material/LocalCafe';
import Checkroom from '@mui/icons-material/Checkroom';
import Diamond from '@mui/icons-material/Diamond';
import MusicNote from '@mui/icons-material/MusicNote';
import Movie from '@mui/icons-material/Movie';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DesignServices from '@mui/icons-material/DesignServices';
import MenuBook from '@mui/icons-material/MenuBook';
import SportsEsports from '@mui/icons-material/SportsEsports';
import Hotel from '@mui/icons-material/Hotel';
import Handshake from '@mui/icons-material/Handshake';

/**
 * The built-in company icon set (AC-25). A company can pick one of these keys
 * instead of uploading a logo; `CompanyAvatar` renders the mapped icon tinted by
 * the company's brand `color`.
 *
 * Keys are stable identifiers persisted on `Company.icon` — never rename one
 * once shipped (existing companies reference it). Add new options at the end.
 * This module is the single source of truth for which keys render; the backend
 * accepts any bounded string and an unknown key simply falls back to initials.
 */
export interface CompanyIconOption {
  /** Persisted key (stored on `Company.icon`). */
  key: string;
  /** Human label for the picker + accessible name. */
  label: string;
  Icon: SvgIconComponent;
}

export const COMPANY_ICONS: readonly CompanyIconOption[] = [
  { key: 'business', label: 'Office', Icon: Business },
  { key: 'corporate', label: 'Corporate', Icon: Domain },
  { key: 'building', label: 'Building', Icon: Apartment },
  { key: 'finance', label: 'Finance', Icon: AccountBalance },
  { key: 'storefront', label: 'Storefront', Icon: Storefront },
  { key: 'retail', label: 'Retail', Icon: ShoppingBag },
  { key: 'manufacturing', label: 'Manufacturing', Icon: Factory },
  { key: 'engineering', label: 'Engineering', Icon: Engineering },
  { key: 'cloud', label: 'Cloud', Icon: Cloud },
  { key: 'software', label: 'Software', Icon: Code },
  { key: 'hardware', label: 'Hardware', Icon: Memory },
  { key: 'network', label: 'Network', Icon: Hub },
  { key: 'startup', label: 'Startup', Icon: RocketLaunch },
  { key: 'energy', label: 'Energy', Icon: Bolt },
  { key: 'sustainability', label: 'Sustainability', Icon: EnergySavingsLeaf },
  { key: 'healthcare', label: 'Healthcare', Icon: LocalHospital },
  { key: 'science', label: 'Science', Icon: Science },
  { key: 'education', label: 'Education', Icon: School },
  { key: 'travel', label: 'Travel', Icon: Flight },
  { key: 'logistics', label: 'Logistics', Icon: LocalShipping },
  { key: 'hospitality', label: 'Hospitality', Icon: Restaurant },
  { key: 'media', label: 'Media', Icon: Campaign },
  { key: 'legal', label: 'Legal', Icon: Gavel },
  { key: 'global', label: 'Global', Icon: Public },
  // ── Expansion batch ──────────────────────────────────────────────────────
  { key: 'analytics', label: 'Analytics', Icon: Analytics },
  { key: 'data', label: 'Data', Icon: DataObject },
  { key: 'api', label: 'API / Platform', Icon: Api },
  { key: 'telecom', label: 'Telecom', Icon: Wifi },
  { key: 'security', label: 'Security', Icon: Security },
  { key: 'property', label: 'Property', Icon: HomeWork },
  { key: 'realestate', label: 'Real estate', Icon: RealEstateAgent },
  { key: 'construction', label: 'Construction', Icon: Construction },
  { key: 'agriculture', label: 'Agriculture', Icon: Agriculture },
  { key: 'automotive', label: 'Automotive', Icon: DirectionsCar },
  { key: 'maritime', label: 'Maritime', Icon: DirectionsBoat },
  { key: 'rail', label: 'Rail', Icon: Train },
  { key: 'biotech', label: 'Biotech', Icon: Biotech },
  { key: 'wellness', label: 'Wellness', Icon: Spa },
  { key: 'fitness', label: 'Fitness', Icon: FitnessCenter },
  { key: 'cafe', label: 'Café', Icon: LocalCafe },
  { key: 'fashion', label: 'Fashion', Icon: Checkroom },
  { key: 'jewellery', label: 'Jewellery', Icon: Diamond },
  { key: 'music', label: 'Music', Icon: MusicNote },
  { key: 'film', label: 'Film', Icon: Movie },
  { key: 'photography', label: 'Photography', Icon: PhotoCamera },
  { key: 'design', label: 'Design', Icon: DesignServices },
  { key: 'publishing', label: 'Publishing', Icon: MenuBook },
  { key: 'gaming', label: 'Gaming', Icon: SportsEsports },
  { key: 'hotel', label: 'Hotel', Icon: Hotel },
  { key: 'consulting', label: 'Consulting', Icon: Handshake },
] as const;

const COMPANY_ICON_MAP: Record<string, CompanyIconOption> = Object.fromEntries(
  COMPANY_ICONS.map((option) => [option.key, option]),
);

/** Resolve a stored icon key to its option, or `undefined` for an unknown key. */
export function companyIconOption(
  key: string | null | undefined,
): CompanyIconOption | undefined {
  return key ? COMPANY_ICON_MAP[key] : undefined;
}

/** Resolve a stored icon key to its component, or `undefined` for an unknown key. */
export function companyIconFor(
  key: string | null | undefined,
): SvgIconComponent | undefined {
  return companyIconOption(key)?.Icon;
}
